import {
  Container,
  Entidade,
  FAB,
  Loading,
  PagedResponse,
  useShowNotification
} from '@elotech/components';
import { AxiosPromise } from 'axios';
import { alert } from 'common/components';
import { useLoading } from 'common/hooks';
import { ProtocoloBaseService } from 'common/service';
import { ProtocoloBaseDTO, ProtocoloEntityRelation } from 'common/type';
import { IntegracaoProcessoEnum, OrigemProtocoloEnum } from 'common/type/enum';
import { FC, useEffect, useState } from 'react';
import { useHistory } from 'react-router';

import { ProcessoDigitalComponent } from './components/processo-digital/ProcessoDigitalComponent';
import { ProtocoloComponent } from './components/processo-protocolo/ProtocoloComponent';

export type ProtocoloContainerPermissions = {
  read: boolean;
  create: boolean;
  update: boolean;
  delete: boolean;
};

const defaultPermissions: ProtocoloContainerPermissions = {
  read: true,
  create: true,
  update: true,
  delete: true
};

type Props = {
  service: ProtocoloBaseService;
  id: string;
  redirectUrl?: string;
  tipoIntegracaoProcessos?: IntegracaoProcessoEnum;
  permissions?: ProtocoloContainerPermissions;
  onEntidadeSearch?: (
    search: string,
    pagination?: any,
    sort?: any
  ) => AxiosPromise<PagedResponse<Entidade>>;
  isInsideScreen?: boolean;
};

export const ProtocoloContainerBase: FC<Props> = ({
  service,
  id,
  redirectUrl,
  tipoIntegracaoProcessos,
  permissions = defaultPermissions,
  onEntidadeSearch,
  isInsideScreen
}) => {
  const [loading, setLoading] = useLoading();
  const [processos, setProcessos] = useState<ProtocoloBaseDTO[]>();
  const history = useHistory();
  const showNotification = useShowNotification();

  useEffect(() => {
    onSearch();
  }, [id]);

  const onSearch = () => {
    setLoading(
      service.findByEntityRelationId(id).then(({ data }) => {
        setProcessos(data);
      })
    );
  };

  const onAddProtocolo = (protocolo: ProtocoloBaseDTO) => {
    service
      .save({
        id: '',
        protocoloId: protocolo.protocoloId,
        origem: protocolo.origem,
        descricao: protocolo.descricao,
        numero: protocolo.numeroIdentificacao,
        ano: protocolo.anoIdentificacao,
        link: protocolo.link,
        entityRelation: { id }
      } as ProtocoloEntityRelation)
      .then(() => {
        onSearch();
        showNotification({
          level: 'success',
          message: 'Protocolo salvo com sucesso!'
        });
      });
  };

  const onAddProcessoDigital = (processoId: string) => {
    onAddProtocolo({
      protocoloId: processoId,
      origem: OrigemProtocoloEnum.PROCESSODIGITAL
    });
  };

  const onFinalizar = () => {
    if (!redirectUrl) {
      return;
    }
    return history.push(redirectUrl);
  };

  const onMarcarPrincipal = (protocoloId: string) => {
    alert({
      type: 'question',
      title: 'Marcar como principal',
      text: 'Deseja marcar o processo/protocolo como principal?',
      onConfirm: () => {
        setLoading(service.marcarPrincipal(protocoloId)).then(() => {
          onSearch();
          showNotification({
            level: 'success',
            message: 'Protocolo marcado como principal!'
          });
        });
      }
    });
  };

  const onPreviewArquivo = (fullPath: string, fileName: string) => {
    setLoading(
      service.gerarUrlParaDownload(fullPath, fileName).then(({ data: url }) => {
        fetch(url.urlAssinada)
          .then(data => data.blob())
          .then(blob => {
            const file = new Blob([blob], { type: 'application/pdf' });
            const fileURL = URL.createObjectURL(file);
            window.open(fileURL);
          });
      })
    );
  };

  const onRemove = (protocoloId: string) => {
    service.remove(protocoloId).then(() => {
      onSearch();
      showNotification({
        level: 'success',
        message: 'Protocolo excluído com sucesso!'
      });
    });
  };

  if (!permissions.read) {
    return null;
  }

  const renderList = () => {
    if (tipoIntegracaoProcessos === IntegracaoProcessoEnum.PROCESSO_DIGITAL) {
      return (
        <ProcessoDigitalComponent
          processos={processos}
          permissions={permissions}
          onAddProcesso={onAddProcessoDigital}
          onMarcarPrincipal={onMarcarPrincipal}
          onRemove={onRemove}
        />
      );
    }

    return (
      <ProtocoloComponent
        protocolos={processos}
        permissions={permissions}
        onAddProtocolo={onAddProtocolo}
        onMarcarPrincipal={onMarcarPrincipal}
        onPreviewArquivo={onPreviewArquivo}
        onRemove={onRemove}
        onEntidadeSearch={onEntidadeSearch}
      />
    );
  };

  if (isInsideScreen) {
    return (
      <>
        <Loading loading={loading} />
        {renderList()}
      </>
    );
  }

  return (
    <Container breadcrumb>
      <Loading loading={loading} />
      {renderList()}
      {redirectUrl && (
        <div className="btn-save">
          <FAB
            icon="check"
            iconColor="white"
            title="Finalizar"
            onClick={onFinalizar}
          />
        </div>
      )}
    </Container>
  );
};

export default ProtocoloContainerBase;
